/**
 * Home page Creators hero — replaces the boring single-row creator strip
 * with 3 daily-rotating category tiles + a bold "Explore N creators" CTA card.
 *
 * Layout: 3 tile cards + 1 CTA card → 4-up grid on desktop, stacks on mobile.
 * Each tile shows the category icon, count, and 3 preview avatars.
 * The CTA card uses a stronger accent color + arrow animation to draw clicks
 * since the dedicated /creators page has way richer content (~150 creators).
 */
import { Link } from "react-router-dom";
import {
  ArrowRight, Plane, ChefHat, Sparkles, Rocket, Smile, Heart, Cpu, Youtube,
  TrendingUp, Newspaper, BookOpen, Dumbbell, Crown, Baby,
} from "lucide-react";

// Per-category visual config — icon + accent gradient
const CATEGORY_CONFIG = {
  "comedy-entertainment":{ icon: Smile,    accent: "from-yellow-700/30 to-yellow-900/10",   iconColor: "text-yellow-400" },
  "food-universe":       { icon: ChefHat,  accent: "from-amber-700/30 to-orange-900/10",    iconColor: "text-amber-400" },
  "tech-knowledge":      { icon: Cpu,      accent: "from-blue-700/30 to-blue-900/10",       iconColor: "text-blue-400" },
  "finance-investing":   { icon: TrendingUp, accent: "from-emerald-700/30 to-emerald-900/10", iconColor: "text-emerald-400" },
  "news-current-affairs":{ icon: Newspaper, accent: "from-red-700/30 to-red-900/10",        iconColor: "text-red-400" },
  "productivity-growth": { icon: Rocket,   accent: "from-emerald-700/30 to-emerald-900/10", iconColor: "text-emerald-400" },
  "travel-stories":      { icon: Plane,    accent: "from-sky-700/30 to-sky-900/10",         iconColor: "text-sky-400" },
  "lifestyle-fashion":   { icon: Heart,    accent: "from-pink-700/30 to-pink-900/10",       iconColor: "text-pink-400" },
  "fitness-wellness":    { icon: Dumbbell, accent: "from-lime-700/30 to-lime-900/10",       iconColor: "text-lime-400" },
  "spiritual-wisdom":    { icon: Sparkles, accent: "from-violet-700/30 to-purple-900/10",   iconColor: "text-violet-400" },
  "mythology-astrology": { icon: BookOpen, accent: "from-orange-700/30 to-rose-900/10",     iconColor: "text-orange-400" },
  "inspiring-women":     { icon: Crown,    accent: "from-fuchsia-700/30 to-fuchsia-900/10", iconColor: "text-fuchsia-400" },
  "kids-family":         { icon: Baby,     accent: "from-cyan-700/30 to-cyan-900/10",       iconColor: "text-cyan-400" },
};

export default function HomeCreatorsHero({ data }) {
  if (!data || !data.tiles || data.tiles.length === 0) return null;
  const tiles = data.tiles.slice(0, 3);
  const total = data.total_channels || 0;
  const totalCategories = data.total_categories || 7;

  return (
    <section className="px-6 lg:px-10 mt-10" data-testid="home-creators-hero">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-1 h-7 bg-[#FF0000] rounded-full" />
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
                <Youtube className="w-5 h-5 text-[#FF0000]" />
                Creator Community
              </h2>
            </div>
          </div>
        </div>

        {/* Grid: 3 tiles + 1 CTA = 4-up */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiles.map((tile) => {
            const cfg = CATEGORY_CONFIG[tile.slug] || CATEGORY_CONFIG["travel-stories"];
            const Icon = cfg.icon;
            return (
              <Link
                key={tile.slug}
                to={`/creators#${tile.slug}`}
                data-testid={`creator-tile-${tile.slug}`}
                className={`group relative rounded-xl border border-white/10 hover:border-white/30 bg-gradient-to-br ${cfg.accent} p-4 transition-colors min-h-[156px] flex flex-col justify-between overflow-hidden`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${cfg.iconColor}`} />
                    <h3 className="font-display text-base font-bold text-white">{tile.title}</h3>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">
                    {tile.count} creators
                  </span>
                </div>

                {/* Avatar previews */}
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex -space-x-2">
                    {(tile.previews || []).slice(0, 3).map((p) => (
                      <div
                        key={p.channel_id}
                        title={p.name}
                        className="w-9 h-9 rounded-full border-2 border-black overflow-hidden bg-neutral-800 flex-shrink-0"
                      >
                        {p.thumb ? (
                          <img src={p.thumb} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-neutral-500 text-sm font-bold">
                            {(p.name || "?")[0]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <span className="text-xs text-neutral-300 ml-1 truncate">
                    {(tile.previews?.[0]?.name) || tile.subtitle}
                  </span>
                </div>
              </Link>
            );
          })}

          {/* ---- The bold CTA tile ---- */}
          <Link
            to="/creators"
            data-testid="creator-explore-all-cta"
            className="group relative rounded-xl border border-[#FF0000]/40 hover:border-[#FF0000] bg-gradient-to-br from-[#FF0000]/20 via-[#FF0000]/[0.08] to-black p-4 min-h-[156px] flex flex-col justify-between overflow-hidden transition-all hover:shadow-[0_0_24px_rgba(255,0,0,0.25)]"
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#FF0000] flex items-center justify-center">
                <Youtube className="w-4 h-4 text-white" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#FF0000] font-bold">
                Explore all
              </span>
            </div>
            <div>
              <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-white leading-tight">
                {total}+ Creators
              </div>
              <div className="text-xs text-neutral-300 mt-1">
                Across {totalCategories} curated worlds — Indian & global mix
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white group-hover:gap-2.5 transition-all">
                Browse all categories
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
